import { useState } from 'react';
import { useAppDispatch } from 'app/hooks';
import { skipToken } from '@reduxjs/toolkit/query';
import { ahuBoardApi, usePatchAhuTunnelMutation } from 'app/services/ahuBoardApi';
import {
  useGetAhuCasingQuery,
  useGetAhuFloorQuery,
  usePatchAhuCasingMutation,
  usePatchAhuFloorMutation,
} from 'app/services/ahuBoardFormsApi';
import type { IAhuBoardTunnel, IAhuBoardUnit, ICmsAhuDoc } from 'models';
import { ahuBoardBus } from '../../bus/ahuBoardBus';
import { validateTunnel } from '../../validation/validationController';
import PaginatedSelect from '../../forms/PaginatedSelect';
import { formatLegacyNumber } from '../../forms/formUtils';
import { cmsWidthValues, cmsHeightValues } from '../../forms/cmsDoc';

/**
 * Phase 6e1 — tunnel cabinet rows (§7.3.1, tab.ahu.tunnel.cabinet.js +
 * tab.ahu.cabinet.tmpl, markup faithful — debug class `2222` dropped per
 * [NEG]#21). W/H selects + readonly L + 4 paginated selects (casing outer
 * skin / skin finish → PATCH casing; floor under-insulation / under-liner →
 * PATCH floor).
 *
 * §19.2 tunnel-save cascade reproduced on PATCH success:
 *   width/height → ahu.validateTunnel + `change:tunnel:dimensions
 *   {tunnel_id, render:true, accessories_fetch:true}` + `changed:tunnel_<id>:sizes`
 *   (+ `changed:tunnel_<id>:width` width-only, + `get:overall:height(true)`
 *   for height) + `tunnelLugs:recalcPlacement` queue (§7.3.1 model listener).
 * Pre-save the cabinet fires `change:tunnel:widthOrHeight` (+ `:<id>`).
 */

interface TunnelCabinetRowProps {
  ahu: IAhuBoardUnit;
  tunnel: IAhuBoardTunnel;
  cms: ICmsAhuDoc | undefined;
  mfgId: number;
}

const TunnelCabinetRow = ({ ahu, tunnel, cms, mfgId }: TunnelCabinetRowProps) => {
  const dispatch = useAppDispatch();
  const [patchTunnel] = usePatchAhuTunnelMutation();
  const [patchCasing] = usePatchAhuCasingMutation();
  const [patchFloor] = usePatchAhuFloorMutation();
  const [emptyRows, setEmptyRows] = useState<Record<number, boolean>>({});

  // Outer-skin / skin-finish live on the CASING row, under-insulation /
  // under-liner on the FLOOR row (legacy tunnel.casing / tunnel.floor models
  // — the tunnel payload does not carry these fields).
  const { data: casing } = useGetAhuCasingQuery(
    tunnel.casing_id != null ? Number(tunnel.casing_id) : skipToken
  );
  const { data: floor } = useGetAhuFloorQuery(
    tunnel.floor_id != null ? Number(tunnel.floor_id) : skipToken
  );

  const widths = cmsWidthValues(cms);
  const heights = cmsHeightValues(cms);
  const ccid = ahu.construction_class_id ?? null;

  // W/H options are CMS value NAMES ("36.00" — value_names is "{:.2f}");
  // tunnel.width is a float (36) — match numerically so the native select's
  // selected option is correct (legacy: tunnel.width.toFixed(2) == name).
  const matchedDim = (values: Array<string | number>, raw: unknown): string => {
    const n = Number(raw);
    const hit = values.find((v) => Number(v) === n);
    return hit != null ? String(hit) : String(raw ?? '');
  };
  const widthValue = matchedDim(widths, tunnel.width);
  const heightValue = matchedDim(heights, tunnel.height);

  const changeTunnel = (name: 'width' | 'height', value: number) => {
    ahuBoardBus.emit('change:tunnel:widthOrHeight', { tunnel_id: tunnel.id, name, value });
    ahuBoardBus.emit(`change:tunnel:widthOrHeight:${tunnel.id}`);
    patchTunnel({ id: tunnel.id, ahuId: tunnel.ahu_id, data: { [name]: value } })
      .unwrap()
      .then(() => {
        // §19.2 tunnel cascade.
        void dispatch(validateTunnel(tunnel.id));
        ahuBoardBus.emit('change:tunnel:dimensions', {
          tunnel_id: tunnel.id,
          render: true,
          accessories_fetch: true,
        });
        ahuBoardBus.emit(`changed:tunnel_${tunnel.id}:sizes`);
        if (name === 'width') ahuBoardBus.emit(`changed:tunnel_${tunnel.id}:width`);
        if (name === 'height') ahuBoardBus.emit('get:overall:height', true);
        ahuBoardBus.emit('tunnelLugs:recalcPlacement', {
          tunnel_id: tunnel.id,
          hide_from_canvas: true,
        });
      })
      .catch(() => undefined);
  };

  const changeCasing = (field: 'outerliner_id' | 'finish_id', id: number | null) => {
    if (tunnel.casing_id == null) return;
    patchCasing({ id: Number(tunnel.casing_id), data: { [field]: id } })
      .unwrap()
      .then(() => {
        // QA5 #7 — write the new id into the tunnel's section rows so the
        // CABINET tab selects update without a refresh (legacy
        // tab.section.js:133-147 listens on tunnel.casing and re-getValues).
        dispatch(
          ahuBoardApi.util.updateQueryData('getTunnelSectionList', tunnel.id, (rows) =>
            rows.map((row) => ({ ...row, [field]: id }))
          )
        );
        if (field === 'outerliner_id') {
          ahuBoardBus.emit('change:tunnel:casing', { tunnel_id: tunnel.id });
          // [NEG]#58 — casing.callValidate passes the CASING id as tunnel id
          // (works because casing_id == tunnel_id in practice); we validate
          // the tunnel directly.
          void dispatch(validateTunnel(tunnel.id));
        }
      })
      .catch(() => undefined);
  };

  const changeFloor = (
    field: 'under_insulation_id' | 'underliner_id',
    id: number | null
  ) => {
    if (tunnel.floor_id == null) return;
    patchFloor({
      id: Number(tunnel.floor_id),
      data: { [field]: id, tunnel_id: tunnel.id },
    })
      .unwrap()
      .then(() => {
        ahuBoardBus.emit(`update:tunnel:${Number(tunnel.floor_id)}:fields`);
        // QA5 #7 — refetch the tunnel's section rows so the CABINET tab's
        // readonly Under-Insulation/Under-Liner rows refresh without a page
        // reload (legacy `update:tunnel:<floorId>:fields` listener,
        // tab.section.js:157-164).
        dispatch(
          ahuBoardApi.util.invalidateTags([{ type: 'AhuSections', id: tunnel.id }])
        );
        if (field === 'under_insulation_id') {
          // under-insulation thickness affects canvas Y (§3.1) — refetch
          // tunnels (the legacy cabinet does model.fetch()).
          dispatch(
            ahuBoardApi.util.invalidateTags([{ type: 'AhuTunnels', id: tunnel.ahu_id }])
          );
        }
      })
      .catch(() => undefined);
  };

  const hiddenIfEmpty = (idx: number) =>
    emptyRows[idx] ? { display: 'none' } : undefined;
  const markEmpty = (idx: number) => (empty: boolean) =>
    setEmptyRows((prev) => (prev[idx] === empty ? prev : { ...prev, [idx]: empty }));

  return (
    <>
      <li className="tunnel-dimensions-li">
        <div className="multiply-total multiply multiply-tunel ">
          <span className="unit">in</span>{' '}
          <span className="selection cabinet-sizes-selection">
            {Number(tunnel.width ?? 0).toFixed(2)}
          </span>{' '}
          <select
            className="small-input tunnel_field required-field"
            name="width"
            value={widthValue}
            onChange={(e) => changeTunnel('width', Number(e.currentTarget.value))}
          >
            {widths.map((w) => (
              <option key={String(w)} value={String(w)}>
                {String(w)}{' '}
              </option>
            ))}
          </select>{' '}
          <span className="unit large"> W </span> <span className="multiply-sign">x</span>{' '}
          <span className="selection cabinet-sizes-selection">
            {Number(tunnel.height ?? 0).toFixed(2)}
          </span>{' '}
          <select
            className="small-input tunnel_field required-field"
            name="height"
            value={heightValue}
            onChange={(e) => changeTunnel('height', Number(e.currentTarget.value))}
          >
            {heights.map((h) => (
              <option key={String(h)} value={String(h)}>
                {String(h)}
              </option>
            ))}
          </select>{' '}
          <span className="unit large"> H </span> <span className="multiply-sign">x</span>{' '}
          <input
            className="small-input tunnel_field"
            type="text"
            data-type="number"
            name="length"
            data-min={tunnel.length_min ?? 12}
            data-max={tunnel.length_max ?? 600}
            data-step="0.01"
            value={formatLegacyNumber(tunnel.length, 0.01)}
            tabIndex={-1}
            readOnly
            onChange={() => undefined}
          />{' '}
          <span className="unit large"> L</span>
        </div>
      </li>
      <li data-select-index="0" style={hiddenIfEmpty(0)}>
        <div className="single-line-with-custom-select">
          <div id="outer-skin-select" className="select-container">
            <PaginatedSelect
              mfgId={mfgId}
              path="ahu/casing/metal_sheets"
              constructionClassId={ccid}
              value={(casing?.outerliner_id as number | null | undefined) ?? null}
              onChange={(id) => changeCasing('outerliner_id', id)}
              selectName="outerliner_id"
              selectClass="tunnel_casing_field"
              hasNoneOption={false}
              onEmptyChange={markEmpty(0)}
            />
          </div>
          <span className="label">Outer Skin</span>
        </div>
      </li>
      <li data-select-index="1" style={hiddenIfEmpty(1)}>
        <div className="single-line-with-custom-select">
          <div id="skin-finish-select" className="select-container">
            <PaginatedSelect
              mfgId={mfgId}
              path="ahu/casing/finishes"
              constructionClassId={ccid}
              value={(casing?.finish_id as number | null | undefined) ?? null}
              onChange={(id) => changeCasing('finish_id', id)}
              selectName="finish_id"
              selectClass="tunnel_casing_field"
              onEmptyChange={markEmpty(1)}
            />
          </div>
          <span className="label">
            Skin Finish
          </span>
        </div>
      </li>
      <li data-select-index="2" style={hiddenIfEmpty(2)}>
        <div className="single-line-with-custom-select">
          <div id="under-insulation-select" className="select-container">
            <PaginatedSelect
              mfgId={mfgId}
              path="ahu/floor/under_insulations"
              constructionClassId={ccid}
              value={(floor?.under_insulation_id as number | null | undefined) ?? null}
              onChange={(id) => changeFloor('under_insulation_id', id)}
              selectName="under_insulation_id"
              selectClass="tunnel_floor_field"
              onEmptyChange={markEmpty(2)}
            />
          </div>
          <span className="label">Floor Under-Insulation</span>
        </div>
      </li>
      <li data-select-index="3" style={hiddenIfEmpty(3)}>
        <div className="single-line-with-custom-select">
          <div id="under-liner-select" className="select-container">
            <PaginatedSelect
              mfgId={mfgId}
              path="ahu/floor/under_liner_metal_sheets"
              constructionClassId={ccid}
              value={(floor?.underliner_id as number | null | undefined) ?? null}
              onChange={(id) => changeFloor('underliner_id', id)}
              selectName="underliner_id"
              selectClass="tunnel_floor_field"
              onEmptyChange={markEmpty(3)}
            />
          </div>
          <span className="label">Floor Under-Liner</span>
        </div>
      </li>
    </>
  );
};

export default TunnelCabinetRow;
